import { useSelector } from 'react-redux';

import { cn } from 'helpers/classnames';
import useMartyContext from 'hooks/useMartyContext';
import { UPS_DROPOFF_LOCATION_URL } from 'constants/appConstants';
import { PROVIDE_LABEL_OPTIONS } from 'constants/exchanges';
import { selectReturnItemCount } from 'selectors/returns';
import ReturnInstructionsSection from 'components/account/Returns/ReturnInstructionsSection';
import ReturnLabelOption from 'components/account/Returns/ReturnLabelOption';
import PrinterIcon from 'images/zappos/printer-icon.svg';
import ReturnLabelButtonsGroup from 'components/account/Returns/ReturnLabelButtonsGroup';
import WarningFilledSmallIcon from 'components/common/IconsV2/WarningFilledSmallIcon';
import External from 'images/zappos/external.svg';

const PrintLabelOption = ({
  labelDelivery,
  customerInfo,
  onReturnMethodSelect,
  showInstructions,
  showInstructionsHandler,
  displayOnly,
  boxInfo,
  postReturnInfo,
  className
}) => {
  const {
    marketplace: { dataMaskPiiElements }
  } = useMartyContext();
  const returnItemCount = useSelector(selectReturnItemCount);

  const checked = labelDelivery === PROVIDE_LABEL_OPTIONS.PRINT;

  const expandedInfo = customerInfo?.email && (
    <div className="mb-2 mt-3">
      <p className="text-xs font-semibold zappos:body-m-medium" data-cs-mask={dataMaskPiiElements}>
        Return label will be sent to: <span className="text-xs font-medium zappos:body-m-medium">{customerInfo.email}</span>
      </p>
    </div>
  );

  const returnInstructionsSection = (
    <ReturnInstructionsSection
      checked={checked}
      displayOnly={displayOnly}
      showInstructions={showInstructions}
      showInstructionsHandler={showInstructionsHandler}
      value={PROVIDE_LABEL_OPTIONS.PRINT}
      storeIcon={PrinterIcon}
    />
  );

  const optionInformation = (
    <>
      <span className="mt-2 text-xs font-bold text-gray-600 zappos:body-m-bold">With shipping box and printed label</span>
      {checked && returnItemCount > 1 ? (
        <div className="my-2 flex gap-x-3">
          <div className="relative top-1 text-icon-warning-light">
            <WarningFilledSmallIcon size={16} />
          </div>
          <p>
            <span className="font-bold zappos:body-m-bold">This will only generate one label.</span> If you have multiple boxes, process a separate
            return for additional items and get a new label.
          </p>
        </div>
      ) : null}
      <p className={cn('text-xs zappos:body-m', { 'block': checked, 'hidden md:hidden': !checked })}>
        Print a return label from home and return your item(s) at any{' '}
        <a href={UPS_DROPOFF_LOCATION_URL} target="_blank" rel="noopener noreferrer">
          UPS locations
          <img className="ml-1 inline-block" src={External} alt="external" />
        </a>
        : The UPS Store, UPS Drop Box, UPS Access Point, Retail Chains, Customer Service Points, Authorized Shipping Outlet.
      </p>
      {checked && expandedInfo}
    </>
  );

  const labelButtonsGroup = (
    <div className="mb-8">
      <a href={UPS_DROPOFF_LOCATION_URL} target="_blank" rel="noopener noreferrer">
        Find UPS Stores <img className="inline-block" src={External} alt={'external'} />
      </a>
      <ReturnLabelButtonsGroup postReturnInfo={postReturnInfo} boxInfo={boxInfo} />
    </div>
  );

  return (
    <ReturnLabelOption
      checked={checked}
      children={returnInstructionsSection}
      icon={PrinterIcon}
      id={PROVIDE_LABEL_OPTIONS.PRINT}
      optionTitle="Drop-Off"
      optionInformation={optionInformation}
      onReturnMethodSelect={onReturnMethodSelect}
      showInstructions={showInstructions}
      showInstructionsHandler={showInstructionsHandler}
      value={PROVIDE_LABEL_OPTIONS.PRINT}
      displayOnly={displayOnly}
      labelButtonsGroup={labelButtonsGroup}
      className={className}
    />
  );
};

export default PrintLabelOption;
